<template>
  <ApplicationWrapper
    @onButtonBackClick="handleButtonBackClick"
    :showTwoActionLines="this.showTwoActionLines"
    @update:actionArea="handleActionAreaChanged"
    :headerBackTooltip="fromMyBookings ? $t('overview.mybookings_header_backToBookingOverview') : $t('overview.details_header_backToCourseOverview')"
    :headerImage="course.CourseImage"
    :headerTitle="course.CourseTitle"
    :disableBackButton="isReservating || isBooking">
    <template slot="details">
      <!-- course images -->
      <v-carousel v-if="needsImageSlider" hide-delimiter-background height="auto">
        <v-carousel-item v-for="(courseImage, index) in course.additionalDetails.CourseImageList" :key="'courseImage_' + index">
          <v-img :src="base64ImgPrefix + courseImage" max-height="350px" contain />
        </v-carousel-item>
      </v-carousel>

      <v-divider v-if="needsImageSlider" class="my-4" />

      <v-list two-line class="nomargin-firstchild nomargin-lastchild">
        <!-- course leader -->
        <Detail
          @onActionIconClick="mailtoCourseLeader"
          :appendIcon="!isNoWs(course.additionalDetails.CourseLeaderEMail) ? 'mdi-email' : ''"
          :prependIcon="isNoWs(leaderDetailsIconOrPortrait) ? 'mdi-account' : ''"
          :prependImage="!isNoWs(leaderDetailsIconOrPortrait) ? leaderDetailsIconOrPortrait : ''"
          :title="course.additionalDetails.CourseLeader"
          :subTitle="$t('overview.details_content_courseLeader')"
          :appendIconTooltip="
            $t(
              'overview.details_content_mailToCourseLeader',
              course.additionalDetails ? [course.additionalDetails.CourseLeader, course.additionalDetails.CourseLeaderEMail] : undefined,
            )
          ">
        </Detail>

        <!-- description -->
        <Detail
          :expandable="true"
          :appendIconTooltip="$t('common.label_copyToClipboard')"
          :prependIcon="'mdi mdi-information-slab-box-outline'"
          :title="$t('common.label_description')">
          <template slot="expansion-content">
            <div v-html="course.CourseDesignation" />
          </template>
        </Detail>

        <!-- course price -->
        <Detail :prependIcon="'mdi-wallet'" :title="contribution" :subTitle="$t('overview.details_content_contribution')" />

        <!-- course availability -->
        <Detail
          :prependIcon="'mdi-calendar-range'"
          :title="getFancyDatestring(course.CourseStartDate, course.CourseEndDate)"
          :subTitle="$t('overview.details_content_availability')" />

        <!-- course load -->
        <Detail :prependIcon="'mdi-account-multiple'" :expandable="true">
          <template slot="expansion-header">
            <div class="tag-container">
              <Tag
                v-for="(tag, index) of course.tags"
                :tagCount="course.tags.length"
                :key="'tag_courseDetail' + course.CourseId + '_' + index"
                :tagInfo="tag" />
            </div>
          </template>

          <template slot="expansion-content">
            <v-list-item dense v-if="course.MaxMemberNumber > 0">
              <!-- member limitation -->
              <v-list-item-content>
                <!-- title  -->
                <v-list-item-title v-if="currentBreakpoint > 1"> {{ $t('overview.details_content_limitation') }} </v-list-item-title>
                <v-list-item-title v-else> {{ $t('overview.details_content_limitation_minmax') }} </v-list-item-title>

                <!-- limitation -->
                <v-list-item-subtitle v-if="currentBreakpoint > 1">
                  {{
                    $t('overview.details_content_minMembers', [course.MinMemberNumber]) +
                    ' — ' +
                    $t('overview.details_content_maxMembers', [course.MaxMemberNumber])
                  }}
                </v-list-item-subtitle>

                <v-list-item-subtitle v-else>
                  {{ course.MinMemberNumber }}
                  &emsp;—&emsp;
                  {{ course.MaxMemberNumber }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <!-- current members -->
            <v-list-item dense>
              <v-list-item-content>
                <!-- title  -->
                <v-list-item-title>{{ $t('overview.details_content_currentMembers') }}</v-list-item-title>

                <!-- members -->
                <v-list-item-subtitle>
                  {{ $t('overview.details_content_members', [course.MemberNumber]) }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>

            <!-- current reservations -->
            <v-list-item dense v-if="SessionData.club.courseConfig.CanCourseReservation">
              <v-list-item-content>
                <!-- title  -->
                <v-list-item-title>{{ $t('overview.details_content_currentReservations') }}</v-list-item-title>

                <!-- reservations -->
                <v-list-item-subtitle>
                  {{ $t('overview.details_content_reservationAmount', [course.ReservationNumber]) }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </template>
        </Detail>

        <!-- meetings -->
        <Detail :prependIcon="'mdi-calendar'" :expandable="true">
          <template slot="expansion-header">
            <v-row no-gutters>
              <v-col cols="4"> {{ $t('overview.details_content_meetings', [course.meetings.length]) }}</v-col>
              <v-col cols="8" class="text--secondary">
                <v-row no-gutters style="width: 100%">
                  <v-spacer />
                  <v-col cols="6"> {{ $t('overview.label_nextMeeting', [getNextMeetingDateCourse(course)]) }} </v-col>
                </v-row>
              </v-col>
            </v-row>
          </template>
          <template slot="expansion-content">
            <v-list-item dense three-line v-for="(meeting, index) in course.meetings" :key="'meeting_' + index">
              <v-list-item-content>
                <!-- meeting location -->
                <v-list-item-title>
                  {{ $t('overview.details_content_meetings_location', [meeting.Location]) }}
                </v-list-item-title>

                <!-- meeting date and time -->
                <v-list-item-subtitle>
                  {{ $d(new Date(meeting.MeetingDate), 'longDate') }}
                  <div v-html="meeting.WholeDay ? $t('overview.meetingWholeDay') : getFancyTimestring(meeting.MeetingTimeFrom, meeting.MeetingTimeTo, true)" />
                </v-list-item-subtitle>
                <v-list-item-subtitle>
                  <b class="green--text">
                    {{ isNextMeetingDate(meeting.MeetingDate) ? $t('common.label_nextMeeting').toUpperCase() : '' }}
                  </b>
                </v-list-item-subtitle>
              </v-list-item-content>

              <!-- whole day indicator -->
              <v-list-item-action v-if="meeting.WholeDay">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon color="primary" dark v-bind="attrs" v-on="on"> mdi-information </v-icon>
                  </template>
                  <span>{{ $t('overview.details_content_meetingWholeDay') }}</span>
                </v-tooltip>
              </v-list-item-action>
            </v-list-item>
          </template>
        </Detail>
      </v-list>
    </template>

    <template slot="actionbuttons" v-if="SessionData.member.permissions.KurseSchreiben">
      <v-row justify="center" no-gutters>
        <!-- remove booking button -->
        <v-btn
          v-if="course.booked && !course.payed"
          @click="handleBookClick(course)"
          :loading="isBooking"
          :disabled="isReservating"
          class="ma-2"
          color="error"
          :style="showTwoActionLines ? 'width: 95%' : ''">
          {{ $t('overview.details_action_removeCourseBooking') }}
        </v-btn>

        <!-- add booking button -->
        <v-btn
          v-else-if="!course.booked && !course.payed && !isBookedOut"
          @click="handleBookClick(course)"
          :loading="isBooking"
          :disabled="isReservating"
          class="ma-2"
          color="primary"
          :style="showTwoActionLines ? 'width: 95%' : ''">
          <div v-if="course.RegularPrice == 0.0" v-text="$t('overview.details_action_addFreeCourseBooking')" />
          <div v-else v-text="$t('overview.details_action_addCourseBooking')" />
        </v-btn>

        <!-- booked out course info -->
        <p v-else-if="isBookedOut" class="text-center ma-0" style="align-self: center; text-wrap: balance">
          {{ $t('common.label_noBookingFullCourses') }}
        </p>

        <!-- already payed coureses cannot be cancelled -->
        <p v-if="course.payed" class="text-center ma-0" style="align-self: center; text-wrap: balance">
          {{ $t('common.label_noUnbookingPayedCourses') }}
        </p>

        <!-- remove reservation button -->
        <v-btn
          v-if="course.reserved && !course.booked && !course.payed"
          @click="handleReservateClick(course)"
          :loading="isReservating"
          :disabled="isBooking"
          class="ma-2"
          color="error"
          :style="showTwoActionLines ? 'width: 95%' : ''">
          {{ $t('overview.details_action_removeCourseReservation') }}
        </v-btn>

        <!-- add reservation button -->
        <v-btn
          v-else-if="!course.reserved && !course.booked && !course.payed"
          @click="handleReservateClick(course)"
          :loading="isReservating"
          :disabled="isBooking"
          class="ma-2"
          color="primary"
          :style="showTwoActionLines ? 'width: 95%' : ''">
          {{ $t('overview.details_action_addCourseReservation') }}
        </v-btn>
      </v-row>
    </template>

    <template slot="actionbuttons" v-else>
      <p class="text-center ma-0" style="align-self: center" v-html="$t('common.label_noBookingPermsCourse')" />
    </template>
  </ApplicationWrapper>
</template>

<script>
// vuex
import { mapGetters } from 'vuex';
import { GET_SESSIONDATA } from '@/store/storeMutator';

// mixins
import HelpersMixin from '@/mixins/helpers.js';
import TagsMixin from '@/mixins/tags.js';
import ResponsiveMixin from '@/mixins/responsive.js';
// components
import ApplicationWrapper from '@/components/controls/ApplicationWrapper.vue';
import Tag from '@/components/controls/Tag.vue';
import Detail from '@/components/controls/Detail.vue';

export default {
  name: 'CourseDetails',

  emits: ['onBookingButtonClick, onReservationButtonClick, onBackButtonClick'],

  props: {
    courseId: String,
    isBooking: Boolean,
    isReservating: Boolean,
    fromMyBookings: Boolean,
  },

  mixins: [HelpersMixin, TagsMixin, ResponsiveMixin],

  components: {
    Tag,
    Detail,
    ApplicationWrapper,
  },

  data: () => {
    return {
      canShowDetails: false,
      showTwoActionLines: undefined,
    };
  },

  methods: {
    handleActionAreaChanged(show) {
      this.showTwoActionLines = show;
    },

    handleBookClick() {
      this.$emit('onBookingButtonClick', this.course);
    },

    handleReservateClick() {
      this.$emit('onReservationButtonClick', this.course);
    },

    handleButtonBackClick() {
      this.$emit('onBackButtonClick');
    },

    mailtoCourseLeader() {
      window.location.href = 'mailto:' + this.course.additionalDetails.CourseLeaderEMail;
    },

    isNextMeetingDate(meetingDateToCompare) {
      var courseNextMeetingDate = this.$d(this.getNextMeetingDateCourse(this.course, true), 'longDate');
      meetingDateToCompare = this.$d(new Date(meetingDateToCompare), 'longDate');
      return courseNextMeetingDate == meetingDateToCompare;
    },
  },

  computed: {
    ...mapGetters({ SessionData: GET_SESSIONDATA }),

    course() {
      return this.SessionData.club.courses.filter((course) => course.CourseId == this.courseId)[0];
    },

    isBookedOut() {
      if (this.course.MaxMemberNumber == 0) return false;

      return this.course.MemberNumber - this.course.MaxMemberNumber == 0;
    },

    /** returns whether the course has images or not (to show/not show the image slider) */
    needsImageSlider() {
      if (!this.course) return false;
      if (!this.course.additionalDetails) return false;
      if (!this.course.additionalDetails.CourseImageList) return false;

      return this.course.additionalDetails.CourseImageList.length > 0;
    },

    /** returns whether the leader portrait or an icon should be shown */
    displayLeaderPortrait() {
      if (!this.SessionData.club.courseConfig.CourseLeaderPortrait) return false;

      return !this.isNoWs(this.course.additionalDetails.CourseLeaderPortrait);
    },

    /**
     * @returns {String} leaderPortrait encoded in base64 (if config allows it and club uploaded an image)
     * @returns {String} otherwise: empty string
     */
    leaderDetailsIconOrPortrait() {
      if (this.displayLeaderPortrait) return this.course.additionalDetails.CourseLeaderPortrait;

      return '';
    },

    /** returns a formatted euro value */
    contribution() {
      var price = this.course.RegularPrice;

      // format
      return price.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
    },
  },

  watch: {
    async course() {
      this.showTwoActionLines = await this.shouldWrapActionsInTwoLines();
    },
  },
};
</script>
